import { useCallback, useEffect, useMemo, useState } from "react";
import useEmblaCarousel from "embla-carousel-react";
import Autoplay from "embla-carousel-autoplay";
import { ChevronLeft, ChevronRight } from "lucide-react";
import type { CarouselSection } from "../../content/industry";
import { Photo } from "./Photo";
import { SectionHeading } from "./SectionHeading";

/** El avance automático nunca corre si el visitante pidió reducir el movimiento. */
function prefersReducedMotion() {
  return typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

/**
 * Carrusel de imágenes accesible sobre Embla Carousel (https://www.embla-carousel.com): Embla resuelve el arrastre con
 * dedo, ratón y trackpad, el bucle y la física del desplazamiento; este componente solo dibuja las diapositivas y los
 * controles (flechas, puntos y teclas izquierda/derecha). El avance automático usa el plugin oficial de Embla y se
 * detiene al pasar el ratón o al enfocar el carrusel con el teclado.
 */
export function Carousel({ section }: { section: CarouselSection }) {
  const count = section.items.length;
  const plugins = useMemo(() => section.autoplay && count > 1 && !prefersReducedMotion()
    ? [Autoplay({ delay: Math.max(3000, section.interval ?? 6000), stopOnMouseEnter: true, stopOnFocusIn: true, stopOnInteraction: false })]
    : [], [section.autoplay, section.interval, count]);
  const [viewport, embla] = useEmblaCarousel({ loop: true, duration: prefersReducedMotion() ? 10 : 25 }, plugins);
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    if (!embla) return;
    const update = () => setCurrent(embla.selectedScrollSnap());
    update();
    embla.on("select", update).on("reInit", update);
    return () => { embla.off("select", update).off("reInit", update); };
  }, [embla]);

  const previous = useCallback(() => embla?.scrollPrev(), [embla]);
  const next = useCallback(() => embla?.scrollNext(), [embla]);

  if (count === 0) return null;

  return (
    <section id={section.id} className="ix-section ix-carousel" aria-labelledby={`${section.id}-title`}>
      <div className="ix-container">
        <SectionHeading id={section.id} eyebrow={section.eyebrow} title={section.title} text={section.text} />
        <div
          className="ix-carousel-frame"
          role="region"
          aria-roledescription="carrusel"
          aria-label={section.title}
          onKeyDown={event => {
            if (event.key === "ArrowLeft") { event.preventDefault(); previous(); }
            if (event.key === "ArrowRight") { event.preventDefault(); next(); }
          }}
        >
          <div ref={viewport} className="ix-carousel-viewport" tabIndex={0}>
            <ul className="ix-carousel-track" aria-live={section.autoplay ? "off" : "polite"}>
              {section.items.map((item, index) => <li
                key={`${item.image}-${item.caption}`}
                className="ix-carousel-slide"
                role="group"
                aria-roledescription="diapositiva"
                aria-label={`${index + 1} de ${count}`}
                aria-hidden={index !== current}
              >
                <figure>
                  <Photo index={item.image} sizes="(min-width: 1200px) 1100px, 100vw" />
                  <figcaption>
                    <span className="ix-carousel-count">{String(index + 1).padStart(2, "0")} / {String(count).padStart(2, "0")}</span>
                    <strong>{item.caption}</strong>
                    {item.text ? <span className="ix-carousel-text">{item.text}</span> : null}
                  </figcaption>
                </figure>
              </li>)}
            </ul>
          </div>
          {count > 1 ? <>
            <button type="button" className="ix-carousel-arrow ix-carousel-arrow--prev" onClick={previous} aria-label="Imagen anterior">
              <ChevronLeft size={22} strokeWidth={1.75} aria-hidden="true" focusable="false" />
            </button>
            <button type="button" className="ix-carousel-arrow ix-carousel-arrow--next" onClick={next} aria-label="Imagen siguiente">
              <ChevronRight size={22} strokeWidth={1.75} aria-hidden="true" focusable="false" />
            </button>
            <div className="ix-carousel-dots">
              {section.items.map((item, index) => <button
                key={`${item.image}-${item.caption}`}
                type="button"
                aria-label={`Ver imagen ${index + 1}: ${item.caption}`}
                aria-current={index === current ? "true" : undefined}
                onClick={() => embla?.scrollTo(index)}
              />)}
            </div>
          </> : null}
        </div>
      </div>
    </section>
  );
}
